/**
 * Tier 1 gating switch — System tab (plans/PLATFORM_PUSH_2026-10-23.md §4).
 *
 * One global dial, OPS_TIER1_GATING, stored in admin_settings and read per
 * ops request by the backend (infrastructure/security/tier1.py). With it on,
 * case names, numbers, file names and member names of an organization that
 * has signed in at least once are masked in this console unless the customer
 * has issued a grant of tier 1 or higher. Owners and admins flip it; every
 * change is an audited settings write.
 */
import { useEffect, useState } from 'react';
import { api } from '../../api';

const KEY = 'OPS_TIER1_GATING';

function isOn(v) {
  if (v === true) return true;
  return ['on', 'true', '1', 'yes'].includes(String(v ?? '').trim().toLowerCase());
}

export default function TierOneSwitch({ opsRole }) {
  const [value, setValue] = useState(null);   // null = not loaded
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const canEdit = opsRole === 'owner' || opsRole === 'admin';

  async function load() {
    try {
      const d = await api.get('/ops/settings');
      const s = d?.settings?.[KEY];
      setValue(isOn(s?.value));
      setSource(s?.source || '');
    } catch (e) {
      setMsg(e?.message || 'Could not read the switch');
    }
  }
  useEffect(() => { load(); }, []);

  async function flip() {
    const next = !value;
    const verb = next ? 'turn ON' : 'turn OFF';
    if (!window.confirm(`${verb} Tier 1 gating for every organization that has signed in? Names and file names ${next ? 'will be masked until a customer grant' : 'become visible to ops again'}.`)) return;
    setBusy(true); setMsg(null);
    try {
      await api.post('/ops/settings', { [KEY]: next });
      await load();
      setMsg(next ? 'Tier 1 gating is ON.' : 'Tier 1 gating is OFF.');
    } catch (e) {
      setMsg(e?.message || 'Could not change the switch');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ops-section" data-testid="tier1-switch">
      <h2>Privacy boundary</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        <strong>Tier 1 gating</strong> — case names, case numbers, court names, file names and member
        names of an organization that has signed in at least once are masked in this console unless
        the customer has issued a grant of tier 1 or higher. Before an organization's first sign-in
        nothing is masked, so onboarding works. Organizations whose admin set “Tier 1 visible to ops”
        to Yes are never masked.
      </p>
      <p style={{ margin: '8px 0' }}>
        Status:{' '}
        {value === null
          ? <span className="muted">loading…</span>
          : <span className={`badge ${value ? 'badge-ok' : ''}`}>{value ? 'ON' : 'OFF'}</span>}
        {source && <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>({source})</span>}
      </p>
      {canEdit && value !== null && (
        <button className={`btn btn-sm ${value ? 'btn-ghost' : 'btn-primary'}`} onClick={flip} disabled={busy}>
          {busy ? 'Saving…' : value ? 'Turn OFF' : 'Turn ON'}
        </button>
      )}
      {msg && <p className="save-msg">{msg}</p>}
    </div>
  );
}
